import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  BadgePercent,
  KeyRound,
  Languages,
  LogOut,
  MessageSquarePlus,
  Monitor,
  Moon,
  Palette,
  Sun,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { displayName } from '../../lib/format'
import { setLanguage, useLanguage } from '../../lib/i18n'
import { usePreference } from '../../lib/preferences'
import { type Theme, themePreference } from '../../lib/theme'
import { buttonClasses } from '../../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'
import { IconButton } from '../../ui/icon-button'
import { toast } from '../../ui/toaster'
import { Tooltip } from '../../ui/tooltip'
import { useLogout, useSession } from '../auth/session'
import { FeedbackDialog } from './feedback-dialog'

const THEME_ICONS = { light: Sun, dark: Moon, system: Monitor } as const

export function LanguageSwitch() {
  const { t } = useTranslation('shell')
  const { language } = useLanguage()
  const next = language === 'ar' ? 'en' : 'ar'
  const label = next === 'ar' ? t('language.switchToArabic') : t('language.switchToEnglish')
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={() => setLanguage(next)}
        aria-label={label}
        lang={next}
        className={buttonClasses('ghost', 'md', 'min-w-8 px-2 font-semibold')}
      >
        {next === 'ar' ? 'ع' : 'EN'}
      </button>
    </Tooltip>
  )
}

export function ThemeMenu({ className }: { className?: string }) {
  const { t } = useTranslation('shell')
  const [theme, setTheme] = usePreference(themePreference)
  const Icon = THEME_ICONS[theme]
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton label={t('theme.label')} icon={<Icon />} className={className} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <ThemeRadioGroup theme={theme} onChange={setTheme} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ThemeRadioGroup({ theme, onChange }: { theme: Theme; onChange: (theme: Theme) => void }) {
  const { t } = useTranslation('shell')
  return (
    <>
      <DropdownMenuLabel>{t('theme.label')}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={theme}
        onValueChange={(v) => onChange(v === 'light' || v === 'dark' ? v : 'system')}
      >
        <DropdownMenuRadioItem value="light">{t('theme.light')}</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="dark">{t('theme.dark')}</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="system">{t('theme.system')}</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

export function useSignOut() {
  const { t } = useTranslation('shell')
  const logout = useLogout()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return {
    pending: logout.isPending,
    signOut: () =>
      logout.mutate(undefined, {
        // Whatever the server says (e.g. the session already expired), the local session is over.
        onSettled: async () => {
          await navigate({ to: '/login', replace: true })
          queryClient.clear()
          toast.success(t('user.signedOut'))
        },
      }),
  }
}

/** `restricted`: while a password change is pending, the menu offers only sign-out. */
export function UserMenu({ restricted = false }: { restricted?: boolean }) {
  const { t } = useTranslation('shell')
  const { language } = useLanguage()
  const session = useSession()
  const navigate = useNavigate()
  const [theme, setTheme] = usePreference(themePreference)
  const { signOut } = useSignOut()
  const name = displayName(session.user, language)
  const tenant = displayName(session.tenant, language)
  const nextLanguage = language === 'ar' ? 'en' : 'ar'
  const { t: tp } = useTranslation('platform')
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('user.menu', { name })}
            className="flex size-(--control-h) items-center justify-center rounded-sm border border-rule-strong bg-surface-sunken text-xs font-semibold text-ink hover:border-ink-muted"
          >
            <span aria-hidden>{initials(name)}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64">
          <div className="px-2 pt-1.5 pb-2">
            <p className="truncate text-sm font-medium text-ink">{name}</p>
            <p className="truncate text-xs text-ink-muted">
              <bdi>{session.user.email}</bdi>
            </p>
            <p className="mt-1 truncate text-xs text-ink-muted">{tenant}</p>
          </div>
          <DropdownMenuSeparator />
          {restricted ? null : (
            <>
              <ThemeRadioGroup theme={theme} onChange={setTheme} />
              <DropdownMenuSeparator />
              <DropdownMenuItem
                icon={<Languages />}
                onSelect={() => setLanguage(nextLanguage)}
                lang={nextLanguage}
              >
                {nextLanguage === 'ar' ? t('language.arabic') : t('language.english')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                icon={<Palette />}
                onSelect={() => void navigate({ to: '/settings/appearance' })}
              >
                {t('user.appearance')}
              </DropdownMenuItem>
              {/* ADR-049: everyone's own commission statements (an earner sees what they earned). */}
              <DropdownMenuItem
                icon={<BadgePercent />}
                onSelect={() => void navigate({ to: '/commissions/mine' })}
              >
                {t('user.myCommission')}
              </DropdownMenuItem>
              {/* Signed in as this user from the console: their password stays theirs (the API refuses it). */}
              {session.impersonatedBy ? null : (
                <DropdownMenuItem
                  icon={<KeyRound />}
                  onSelect={() => void navigate({ to: '/change-password' })}
                >
                  {t('user.changePassword')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem icon={<MessageSquarePlus />} onSelect={() => setFeedbackOpen(true)}>
                {tp('feedbackForm.menu')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem icon={<LogOut />} onSelect={signOut}>
            {t('user.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {restricted ? null : <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />}
    </>
  )
}
